import { defineStore } from 'pinia';
import { auth, db } from '../firebase';
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile as updateFirebaseProfile,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const defaultSettings = {
    notifications: true,
    digestFrequency: 'weekly',
    timezone: 'Local',
    shareProgress: false
};

export const useAuthStore = defineStore('auth', {
    state: () => ({
        status: 'anonymous', // 'anonymous', 'authenticated', 'loading'
        user: null, // Firebase User object (serialized-ish)
        profile: {
            displayName: 'Reader',
            email: '',
            readingFocus: 'Whole Bible',
            startTime: 'morning',
            preferredBibleApp: 'YouVersion',
            preferredTranslation: 'KJV'
        },
        settings: { ...defaultSettings },
        savedPlans: [],
        progress: {},
        subscription: {
            tier: 'free', // 'free' | 'premium_monthly' | 'premium_yearly' | 'premium_lifetime' | 'intro_lifetime'
            status: 'active', // 'active', 'canceled', 'expired'
            expiresAt: null
        },
        authInitialized: false
    }),
    getters: {
        isAuthenticated: (state) => state.status === 'authenticated',
        displayName: (state) => state.profile?.displayName || state.user?.displayName || 'Reader',
        userId: (state) => state.user?.uid,
        isPremium: (state) => {
            if (state.status !== 'authenticated') return false;
            return state.subscription &&
                state.subscription.tier !== 'free' &&
                state.subscription.status === 'active';
        },
        currentTier: (state) => state.subscription?.tier || 'free'
    },
    actions: {
        init() {
            if (this.authInitialized) return;

            onAuthStateChanged(auth, async (user) => {
                if (user) {
                    this.status = 'authenticated';
                    this.user = {
                        uid: user.uid,
                        email: user.email,
                        displayName: user.displayName,
                        photoURL: user.photoURL
                    };

                    // Reset state to defaults before loading new user data to prevent leakage
                    this.subscription = { tier: 'free', status: 'active', expiresAt: null };
                    this.savedPlans = [];
                    this.settings = { ...defaultSettings };
                    // Reset profile but default fields are set below anyway
                    this.profile = {
                        displayName: 'Reader',
                        email: '',
                        readingFocus: 'Whole Bible',
                        startTime: 'morning',
                        preferredBibleApp: 'YouVersion',
                        preferredTranslation: 'KJV'
                    };

                    this.profile.email = user.email;
                    this.profile.displayName = user.displayName || 'Reader';

                    // Fetch extended profile/settings from Firestore
                    try {
                        const userDocRef = doc(db, 'users', user.uid);
                        const userDoc = await getDoc(userDocRef);

                        if (userDoc.exists()) {
                            const data = userDoc.data();
                            if (data.profile) {
                                this.profile = { ...this.profile, ...data.profile };

                                // Sync translation preference
                                if (this.profile.preferredTranslation) {
                                    const { useUserPreferencesStore } = await import('./userPreferences');
                                    const prefs = useUserPreferencesStore();
                                    prefs.translation = this.profile.preferredTranslation;
                                    prefs.save();
                                }
                            }
                            if (data.settings) {
                                this.settings = { ...this.settings, ...data.settings };
                            }
                            if (data.savedPlans) {
                                this.savedPlans = data.savedPlans;
                            }
                            if (data.progress) {
                                this.progress = data.progress;
                            }
                            // Sync subscription data
                            if (data.subscription) {
                                this.subscription = data.subscription;
                            } else {
                                // Missing subscription data, set default and save
                                console.log('User has no subscription data, setting to default free tier.');
                                this.subscription = { tier: 'free', status: 'active', expiresAt: null };
                                await this.saveUserDoc();
                            }

                            // Sync/Migrate to ProgressStore
                            const { useProgressStore } = await import('./progress');
                            const progressStore = useProgressStore();
                            if (data.progress) {
                                await progressStore.migrateLocalToFirestore(data.progress);
                            } else {
                                // No remote progress, but we might have local.
                                // Calling migrate with empty obj triggers push of local to remote
                                await progressStore.migrateLocalToFirestore({});
                            }
                            // Store onboarding status
                            if (data.onboardingCompleted) {
                                this.user.onboardingCompleted = true;
                            }
                        } else {
                            // User authenticated but no Firestore doc exists yet.
                            // Create one with defaults including Free subscription.
                            console.log('User document missing, creating new default profile with free tier.');
                            await this.saveUserDoc();
                        }
                    } catch (e) {
                        console.error("Failed to fetch user profile", e);
                    }
                } else {
                    this.status = 'anonymous';
                    this.user = null;
                    // Reset to defaults
                }
                this.authInitialized = true;
            });
        },
        async updateProgress(newProgress) {
            this.progress = newProgress;
            if (this.user) {
                await this.saveUserDoc();
            }
        },
        async login({ email, password }) {
            try {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                return userCredential.user;
            } catch (error) {
                console.error("Login failed:", error);
                throw error;
            }
        },
        async register({ email, password }) {
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                // Initialize local state immediately so we can save
                this.status = 'authenticated';
                this.user = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || 'Reader',
                    photoURL: user.photoURL
                };
                this.profile.email = user.email;

                // create the document in firestore
                await this.saveUserDoc();

                return userCredential.user;
            } catch (error) {
                console.error("Registration failed:", error);
                throw error;
            }
        },
        async resetPassword(email) {
            try {
                await sendPasswordResetEmail(auth, email);
            } catch (error) {
                console.error("Password reset failed:", error);
                throw error;
            }
        },
        async logout() {
            try {
                await signOut(auth);
                this.status = 'anonymous';
                this.user = null;
                this.subscription = { tier: 'free', status: 'active', expiresAt: null };
                this.savedPlans = [];
                this.settings = { ...defaultSettings };
                this.profile = {
                    displayName: 'Reader',
                    email: '',
                    readingFocus: 'Whole Bible',
                    startTime: 'morning',
                    preferredBibleApp: 'YouVersion',
                    preferredTranslation: 'KJV'
                };
            } catch (error) {
                console.error("Logout failed:", error);
            }
        },
        async updateProfile(updates) {
            this.profile = { ...this.profile, ...updates };

            if (this.user) {
                // Update Auth Profile
                if ((updates.displayName && updates.displayName !== this.user.displayName) || updates.photoURL) {
                    try {
                        await updateFirebaseProfile(auth.currentUser, {
                            displayName: updates.displayName || auth.currentUser.displayName,
                            photoURL: updates.photoURL
                        });
                        this.user.displayName = auth.currentUser.displayName;
                    } catch (e) {
                        console.error("Failed to update Firebase profile", e);
                    }
                }

                // Update Firestore
                await this.saveUserDoc();
            }
        },
        async updateSettings(updates) {
            this.settings = { ...this.settings, ...updates };
            if (this.user) {
                await this.saveUserDoc();
            }
        },
        async toggleSavePlan(plan) {
            if (!this.user) return;

            const idx = this.savedPlans.findIndex(p => p.id === plan.id);
            if (idx >= 0) {
                this.savedPlans.splice(idx, 1);
            } else {
                // Save minimal data necessary for card display
                this.savedPlans.push({
                    id: plan.id,
                    name: plan.name,
                    timeCommitment: plan.timeCommitment || 'N/A',
                    paceTag: plan.paceTag || 'N/A',
                    focusTags: plan.focusTags || [],
                });
            }
            await this.saveUserDoc();
        },

        async upgradeSubscription(tierId) {
            if (!this.user) return;

            // In a real app, verify payment first via backend/webhook
            // For now, we trust the client-side call after "payment" succeeds
            this.subscription = {
                tier: tierId,
                status: 'active',
                expiresAt: null // null mean forever or auto-renew for now
            };

            await this.saveUserDoc();
        },

        async saveUserDoc() {
            if (!this.user) return;
            try {
                const userDocRef = doc(db, 'users', this.user.uid);
                await setDoc(userDocRef, {
                    profile: this.profile,
                    settings: this.settings,
                    savedPlans: this.savedPlans,
                    progress: this.progress,
                    subscription: this.subscription,
                    updatedAt: new Date()
                }, { merge: true });
            } catch (e) {
                console.error("Failed to save user doc", e);
            }
        },
        async waitForAuth() {
            return new Promise(resolve => {
                const check = () => {
                    if (this.authInitialized) resolve();
                    else setTimeout(check, 50);
                }
                check();
            });
        }
    }
});
